import { Box, Divider, Grid, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import theme from "../../theme";
import CommonButton from "../../common/components/CommonButton";
import { Add } from "@mui/icons-material";
import { Academics, ActivitiesAt, ActivitiesStatus, DisplayGrade, StudentGrade, UserTypeId } from "../../common/const";
import CommonDialog from "../../common/components/CommonDialog";
import LocationSearch from "../../common/components/LocationSearch";
import CommonFormField from "../../common/components/CommonFormField";
import CommonSelect from "../../common/components/CommonSelect";
import moment from "moment";
import { setLoading } from "../../features/authSlice";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { useDispatch, useSelector } from "react-redux";
import { createActivity, getActivities, updateActivity } from "../../api/activityApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import CommonGradeButton from "./CommonGradeButton";

const Activity = () => {
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [filter, setFilter] = useState('OUTSIDE');
    const [currentGrade, setCurrentGrade] = useState(user?.student_current_grade);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [activities, setActivities] = useState([]);
    const [activity, setActivity] = useState([]);
    const [formData, setFormData] = useState({
        curricular: {
            activityAt: ActivitiesAt[0].value,
            activityName: '',
            noofhours: '',
            status: '',
            startdate: '',
            enddate: '',
            activityStatus: '',
            activityLocation: '',
        }
    });

    const validateField = (name, value) => {
        switch (name) {
            case 'activityName':
                if (!value) return 'Activity Name is required';
                return '';
            case 'startdate':
                if (!value) return 'Start Date is required';
                return '';
            case 'status':
                if (!value) return 'Status is required';
                return '';
            case 'noofhours':
                if (!value && value !== 0) return 'Hours are required';
                if (value < 0) return 'Hours cannot be negative';
                if (value > 10000) return 'Hours cannot exceed 10000';
                if (value === 0) return 'Hours must be greater than 0';
                return '';
            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Special handling for noofhours to prevent exceeding max
        if (name === 'noofhours') {
            const numValue = Number(value);
            if (numValue > 10000) {
                setErrors(prev => ({ 
                    ...prev, 
                    noofhours: 'Hours cannot exceed 10000' 
                }));
                return;
            }
            if (numValue < 0) {
                setErrors(prev => ({ 
                    ...prev, 
                    noofhours: 'Hours cannot be negative' 
                }));
                return;
            }
        }
        
        setFormData((prev) => ({
            ...prev,
            curricular: {
                ...prev.curricular,
                [name]: value,
            },
        }));

        // Clear error for the field being changed
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData.curricular).forEach(key => {
            const error = validateField(key, formData.curricular[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            
            // Additional hours validation before submission
            const hours = Number(formData.curricular.noofhours);
            if (hours > 10000) {
                showNotification('Contributed hours cannot exceed 10000', 'error');
                return;
            }
            if (hours < 0) {
                showNotification('Contributed hours cannot be negative', 'error');
                return;
            }
            if (hours === 0) {
                showNotification('Contributed hours must be greater than 0', 'error');
                return;
            }
            
            const data = {
                "activity_name": formData.curricular.activityName,
                "activity_location": ActivitiesAt.find((a) => a.value === formData.curricular.activityAt).label?.toUpperCase(), //SCHOOL, OUTSIDE
                "google_location": formData.curricular.activityLocation !== '' ? formData.curricular.activityLocation : null,
                "contribution_hours": formData.curricular.noofhours,
                "status": formData.curricular.status,
                "activity_status": formData.curricular.activityStatus,
                "start_date": formData.curricular.startdate !== '' ? new Date(formData.curricular.startdate).toISOString() : null,
                "end_date": formData.curricular.enddate !== '' ? new Date(formData.curricular.enddate).toISOString() : null,
                "student_grade": currentGrade,
                "student_id": user.student_id
            };
            let subjecResponse;
            dispatch(setLoading({
                loading: true
            }));
            if (formData.curricular?.extra_curricular_activity_id && formData.curricular?.extra_curricular_activity_id !== '') {
                subjecResponse = await updateActivity(formData.curricular?.extra_curricular_activity_id, data);
            } else {
                subjecResponse = await createActivity(data);
            }
            if (subjecResponse) {
                dispatch(setLoading({
                    loading: false
                }));
                if (formData.curricular?.extra_curricular_activity_id !== '') {
                    showNotification('Extra curricular activity updated successfully', 'success');
                } else {
                    showNotification('Extra curricular activity added successfully', 'success');
                }
                setDialogOpen(false);
                getActivity();
                setDialogOpen(false);
                setFormData({
                    curricular: {
                        extra_curricular_activity_id: '',
                        activityAt: ActivitiesAt[0].value,
                        activityName: '',
                        noofhours: '',
                        status: '',
                        startdate: '',
                        enddate: '',
                        activityStatus: '',
                        activityLocation: '',
                    }
                });
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            dispatch(setLoading({
                loading: false
            }));
            showNotification(errorMessage, 'error');
        }
    }

    const getActivity = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const subjectRes = await getActivities(user?.student_id, currentGrade);
            if (currentGrade === null) {
                setCurrentGrade(subjectRes?.data?.[0]?.student_grade || StudentGrade[0].value);
            }
            setActivities(subjectRes.data);
            setActivity(subjectRes.data.filter((a) => a.activity_location === filter));
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.student_id) {
            getActivity();
        }
    }, [user?.student_id, filter, currentGrade]);

    const getMinDate = () => {
        const date = formData.curricular.startdate ? new Date(formData.curricular.startdate) : new Date();
        date.setDate(date.getDate() + 1);
        return date.toISOString().split('T')[0];
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={dialogOpen}
                size="sm"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{formData.curricular?.extra_curricular_activity_id && formData.curricular?.extra_curricular_activity_id !== '' ? 'Edit Activity' : 'Add Activity'}</Typography>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container spacing={1.5} mt={0.5} mb={2}>
                        <Grid item xs={12} md={12}>
                            <CommonSelect
                                label="Activities At"
                                name="activityAt"
                                value={formData.curricular.activityAt}
                                onChange={handleInputChange}
                                placeholder="Select Activities At"
                                options={ActivitiesAt}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <CommonFormField
                                label="Activity Name"
                                type="email"
                                name="activityName"
                                value={formData.curricular.activityName}
                                onChange={handleInputChange}
                                error={errors.activityName}
                                placeholder="e.g., Team Captain"
                            />
                        </Grid>
                        {formData.curricular.activityAt === 2 && (
                            <Grid item xs={12} md={12}>
                                <LocationSearch
                                    label="Location"
                                    value={formData.curricular.activityLocation}
                                    onChange={(selectedLocation) => {
                                        setFormData(prev => ({
                                            ...prev,
                                            curricular: {
                                                ...prev.curricular,
                                                activityLocation: selectedLocation
                                            }
                                        }));
                                    }}
                                    placeholder="Select Location"
                                    width='550px'
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} md={6}>
                            <CommonFormField
                                label="Contributed Number of Hours/Week"
                                type="number"
                                name="noofhours"
                                value={formData.curricular.noofhours}
                                onChange={handleInputChange}
                                placeholder="Enter Hours (Max 10000)"
                                error={errors.noofhours}
                                inputProps={{
                                    min: 1,
                                    max: 10000,
                                    step: 1
                                }}
                                onKeyDown={(e) => {
                                    // Prevent typing 'e', 'E', '-', and other invalid characters
                                    if (e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+' || e.key === '.') {
                                        e.preventDefault();
                                    }
                                }}
                                onInput={(e) => {
                                    // Additional validation: prevent values > 10000 while typing
                                    const value = parseInt(e.target.value);
                                    if (value > 10000) {
                                        e.target.value = 10000;
                                        handleInputChange({ target: { name: 'noofhours', value: 10000 } });
                                    }
                                    if (value < 0) {
                                        e.target.value = 1;
                                        handleInputChange({ target: { name: 'noofhours', value: 1 } });
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <CommonSelect
                                label="Status"
                                name="status"
                                value={formData.curricular.status}
                                onChange={handleInputChange}
                                placeholder="Select Status"
                                error={errors.status}
                                options={Academics}
                            />
                        </Grid>
                        <Grid item xs={12} md={formData.curricular.status === 'PAST' ? 6 : 12}>
                            <CommonFormField
                                label="Start Date"
                                type="date"
                                name="startdate"
                                value={formData.curricular.startdate}
                                onChange={handleInputChange}
                                error={errors.startdate}
                                placeholder="Select Start Date"
                            />
                        </Grid>
                        {formData.curricular.status === 'PAST' && (
                            <Grid item xs={12} md={6}>
                                <CommonFormField
                                    label="End Date"
                                    type="date"
                                    name="enddate"
                                    value={formData.curricular.enddate}
                                    onChange={handleInputChange}
                                    placeholder="Select End Date"
                                    minDate={getMinDate()}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} md={12}>
                            <CommonSelect
                                label="Activity Status"
                                name="activityStatus"
                                value={formData.curricular.activityStatus}
                                onChange={handleInputChange}
                                placeholder="Completed/ In Progress"
                                options={ActivitiesStatus}
                            />
                        </Grid>
                    </Grid>
                }
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Cancel"
                            size="small"
                            background='white'
                            color={theme.palette.secondary.main}
                            border={`1px solid ${theme.palette.background.secondary}`}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={async () => {
                                setFormData({
                                    curricular: {
                                        activityAt: ActivitiesAt[0].value,
                                        activityName: '',
                                        noofhours: '',
                                        status: '',
                                        startdate: '',
                                        enddate: '',
                                        activityStatus: '',
                                        activityLocation: '',
                                    }
                                });
                                setErrors({});
                                setDialogOpen(false)
                            }}
                        />
                        <CommonButton
                            label={formData.curricular?.extra_curricular_activity_id && formData.curricular?.extra_curricular_activity_id !== '' ? 'Edit Activity' : 'Add Activity'}
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.background.main}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={() => {
                                handleSubmit();
                            }}
                        />
                    </Fragment>
                )}
            />
            <CommonCard
                borderRadius={1}
                border={`1px solid ${theme.palette.background.secondary}`}
                paddingBottom="0px"
                padding="0px"
                marginTop="0px"
                headerContent={
                    <Box>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            p={2}
                        >
                            <Box>
                                <Typography fontSize={22} fontWeight={600}>My Extra Curricular Activities</Typography>
                                <Typography fontSize={15} color={theme.palette.background.dark}>Manage My Extra Curricular Activities</Typography>
                            </Box>
                            {user?.user_type_id === UserTypeId.Student && (
                                <CommonButton
                                    label="Add Extra Curricular Activities"
                                    width="280px"
                                    marginBottom="0px"
                                    fontWeight={600}
                                    startIcon={<Add />}
                                    handleClick={() => setDialogOpen(true)}
                                />
                            )}
                        </Box>
                        <Divider />
                    </Box>
                }
                content={
                    <Fragment>
                        <Grid container spacing={1} sx={{ padding: '13px' }}>
                            <Grid item xs={12} md={12} display="flex" gap={1}>
                                <CommonGradeButton
                                    currentGrade={currentGrade}
                                    setCurrentGrade={setCurrentGrade}
                                />
                            </Grid>
                        </Grid>
                        <Box
                            display="flex"
                            gap={1}
                            width="50%"
                            padding={2}
                        >
                            <CommonButton
                                label={`OutSide (${activities.filter((a) => a.activity_location === 'OUTSIDE').length})`}
                                size="small"
                                height="35px"
                                marginBottom="0px"
                                fontWeight="bold"
                                width="20%"
                                background={filter === 'OUTSIDE' ? theme.palette.primary.main : theme.palette.background.main}
                                color={filter === 'OUTSIDE' ? theme.palette.secondary.main : theme.palette.background.dark}
                                handleClick={() => {
                                    setFilter('OUTSIDE');
                                }}
                                border={filter !== 'OUTSIDE' ? "1px solid lightgray" : 'none'}
                            />
                            <CommonButton
                                label={`School (${activities.filter((a) => a.activity_location === 'SCHOOL').length})`}
                                size="small"
                                height="35px"
                                marginBottom="0px"
                                fontWeight="bold"
                                width="20%"
                                background={filter === 'SCHOOL' ? theme.palette.primary.main : theme.palette.background.main}
                                color={filter === 'SCHOOL' ? theme.palette.secondary.main : theme.palette.background.dark}
                                handleClick={() => {
                                    setFilter('SCHOOL');
                                }}
                                border={filter !== 'SCHOOL' ? "1px solid lightgray" : 'none'}
                            />
                        </Box>
                        <Grid container spacing={1} sx={{ padding: '13px', marginBottom: 1 }}>
                            {activity?.map((a, index) => (
                                <Grid item md={6} key={index}>
                                    <CommonCard
                                        borderRadius={1}
                                        border={`1px solid ${theme.palette.background.secondary}`}
                                        paddingBottom="0px"
                                        padding="13px"
                                        marginTop="0px"
                                        content={
                                            <Fragment>
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                >
                                                    <Typography fontWeight={600}>{a.activity_name}</Typography>
                                                    <Box
                                                        display="flex"
                                                        gap={1}
                                                    >
                                                        {user?.user_type_id === UserTypeId.Student && (
                                                            <CommonButton
                                                                label="Edit"
                                                                background="white"
                                                                marginBottom="0px"
                                                                border={`1px solid ${theme.palette.background.secondary}`}
                                                                height="30px"
                                                                width="50px"
                                                                fontWeight={600}
                                                                handleClick={() => {
                                                                    setDialogOpen(true);
                                                                    setFormData((prev) => ({
                                                                        ...prev,
                                                                        curricular: {
                                                                            ...prev.curricular,
                                                                            extra_curricular_activity_id: a.extra_curricular_activity_id,
                                                                            activityAt: ActivitiesAt?.find((i) => i?.label.toUpperCase() === a?.activity_location)?.value,
                                                                            activityName: a.activity_name,
                                                                            noofhours: a.contribution_hours,
                                                                            status: a.status,
                                                                            startdate: a.start_date !== null ? moment(a.start_date)?.format('YYYY-MM-DD') : '',
                                                                            enddate: a.end_date !== null ? moment(a.end_date)?.format('YYYY-MM-DD') : '',
                                                                            activityStatus: a.activity_status,
                                                                            activityLocation: a.google_location,
                                                                        },
                                                                    }));
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Typography color={theme.palette.background.dark}>{a?.google_location?.label}</Typography>
                                                <Box
                                                    display="flex"
                                                    gap={1}
                                                    mt={1}
                                                >
                                                    <Box
                                                        display="flex"
                                                        gap={1}
                                                        border={`1px solid ${theme.palette.background.secondary}`}
                                                        borderRadius={0.6}
                                                        padding="0px 5px"
                                                    >
                                                        <Typography fontSize={14}>{DisplayGrade[currentGrade]}</Typography>
                                                    </Box>
                                                    <Box
                                                        display="flex"
                                                        gap={1}
                                                        border={`1px solid ${theme.palette.background.secondary}`}
                                                        borderRadius={0.6}
                                                        padding="0px 5px"
                                                    >
                                                        <Typography fontSize={14}>{Academics.find((i) => i.value === a.status)?.label}</Typography>
                                                    </Box>
                                                    <Box
                                                        display="flex"
                                                        gap={1}
                                                        border={`1px solid ${theme.palette.background.secondary}`}
                                                        borderRadius={0.6}
                                                        padding="0px 5px"
                                                    >
                                                        <Typography fontSize={14}>{`${a.contribution_hours} hours/week`}</Typography>
                                                    </Box>
                                                    <Box
                                                        border={`1px solid ${theme.palette.background.secondary}`}
                                                        borderRadius={0.6}
                                                        padding="0px 5px"
                                                        sx={{
                                                            background: a.activity_status === 'INPROGRESS' ? theme.palette.secondary.main : '#01875F',
                                                        }}
                                                    >
                                                        <Typography fontSize={14} color={a.activity_status === 'INPROGRESS' ? theme.palette.primary.main : 'white'}>{a.activity_status ? ActivitiesStatus.find((i) => i.value === a.activity_status)?.label : '-'}</Typography>
                                                    </Box>
                                                </Box>
                                                <Box
                                                    display="flex"
                                                    gap={10}
                                                    mt={1}
                                                >
                                                    <Box>
                                                        <Typography fontSize={14} fontWeight={600}>{a.start_date ? moment(a.start_date).format('DD MMM, YYYY') : '-'}</Typography>
                                                        <Typography fontSize={13} color={theme.palette.background.dark}>Start Date</Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography fontSize={14} fontWeight={600}>{a.end_date ? moment(a.end_date).format('DD MMM, YYYY') : '-'}</Typography>
                                                        <Typography fontSize={13} color={theme.palette.background.dark}>End Date</Typography>
                                                    </Box>
                                                </Box>
                                            </Fragment>
                                        }
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Fragment>
                }
            />
        </Fragment>
    )
}

export default Activity;